import React from "react";

const MinimalistElegantQuotation = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        expiryDate,
        items,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-20 font-serif text-slate-700 leading-relaxed min-h-[1100px] flex flex-col justify-between border-[20px] border-slate-50">
            <div>
                {/* Header */}
                <div className="flex justify-between items-start mb-24">
                    <div className="space-y-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">Quotation From</div>
                        <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase italic leading-none">{companyDetails?.name}</h1>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 max-w-[200px] italic">{companyDetails?.address}</p>
                    </div>

                    <div className="text-right space-y-4">
                        <div className="text-6xl font-black text-slate-50 uppercase tracking-tighter leading-none mb-4 italic">ESTIMATE</div>
                        <div className="space-y-1">
                            <div className="text-xs font-black text-slate-900 uppercase tracking-widest leading-none underline underline-offset-4 decoration-slate-900 decoration-2 italic"># {title}</div>
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.3em] italic">Created: {date}</div>
                        </div>
                    </div>
                </div>

                {/* Receiver Info */}
                <div className="mb-24 flex justify-between items-end border-b border-slate-100 pb-16">
                    <div className="space-y-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">Quotation For</div>
                        <div className="space-y-1">
                            <div className="text-2xl font-black text-slate-900 tracking-tighter italic uppercase underline underline-offset-8 decoration-slate-900 decoration-4">{to}</div>
                            <div className="text-sm font-medium text-slate-500 italic uppercase tracking-widest pt-2">{clientAddress}</div>
                        </div>
                    </div>
                    <div className="text-right space-y-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">
                        <div>Offer Valid Until</div>
                        <div className="text-slate-900 font-black text-xs italic">{expiryDate}</div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-16">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300 border-b border-slate-100 whitespace-nowrap overflow-hidden text-ellipsis">
                                <th className="py-6 italic">Project Particulars</th>
                                <th className="py-6 text-center italic">Qty</th>
                                <th className="py-6 text-right italic">Estimated Rate</th>
                                <th className="py-6 text-right italic">Estimated Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 italic text-xs uppercase tracking-tighter">
                            {(items || []).map((item, idx) => (
                                <tr key={idx}>
                                    <td className="py-10">
                                        <div className="text-lg font-black text-slate-900 tracking-tighter italic uppercase whitespace-nowrap overflow-hidden text-ellipsis">{item.item}</div>
                                        <div className="text-[10px] text-slate-400 mt-2 italic underline underline-offset-4 decoration-slate-100 decoration-1">Estimate Ref: 00{idx + 1}</div>
                                    </td>
                                    <td className="py-10 text-center text-slate-400 font-bold">{item.qty}</td>
                                    <td className="py-10 text-right text-slate-900 font-black">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="py-10 text-right text-lg font-black text-slate-900 border-l border-slate-50 pl-6">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Section */}
            <div>
                <div className="flex justify-between items-start mb-16 border-t border-slate-100 pt-16">
                    <div className="max-w-[300px] space-y-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 italic whitespace-nowrap overflow-hidden text-ellipsis">Proposal Remarks</div>
                        <p className="text-[10px] font-medium leading-loose text-slate-400 uppercase tracking-widest italic border-l-2 border-slate-900 pl-4">
                            {notes || "We believe in transparency and value. This estimate covers the core requirements."}
                            <br /><br />
                            {terms || "Acceptance of this quotation is required within the validity period."}
                        </p>
                    </div>

                    <div className="w-64 space-y-4 font-black uppercase tracking-widest text-[10px] italic">
                        <div className="flex justify-between text-slate-300">
                            <span>Subtotal</span>
                            <span className="text-slate-900">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                            <span>Tax ({taxRate}%)</span>
                            <span className="text-slate-900">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-slate-400">
                                <span>Discount Offset</span>
                                <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="pt-8 mt-8 border-t border-slate-900 flex justify-between items-end">
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-900">Final Estimate</span>
                            <div className="text-4xl font-black text-slate-900 tracking-tighter italic leading-none whitespace-nowrap overflow-hidden text-ellipsis">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                {/* Signing Area */}
                <div className="flex justify-between items-end pt-10 border-t border-slate-100">
                    <div className="space-y-4">
                        <div className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-200">Confirmation Stamp</div>
                        <div className="text-[10px] font-bold text-slate-300 underline underline-offset-4 decoration-slate-100 italic">{companyDetails?.name}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-200 mb-8 italic">Authorized Estimator</div>
                        <div className="w-40 h-[2px] bg-slate-900"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MinimalistElegantQuotation;
